import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { FormState, FieldState, Validator } from 'formstate';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';

import { currency } from 'src/util/formatters';
import { lazyInject } from '../../ioc';
import { InvoiceStore } from '../../stores/invoice.store';
import { RouterStore } from 'src/core/stores/router.store';

import { PaymentAmount } from './desktop/payment-amount';

export class PaymentAmountFormState {
    @lazyInject(InvoiceStore) private invoiceStore!: InvoiceStore;
    @lazyInject(RouterStore) private router!: RouterStore;

    @observable errorMessage: string | undefined;
    @observable fullBalance: number | undefined;
    @observable partialPaymentEnabled: boolean | undefined;
    @observable onStateChanged?: () => void | undefined;

    customAmountValidator: Validator<string> = (value) => {
        if (this.onStateChanged) {
            this.onStateChanged();
        }

        if (this.isFullBalance.value) {
            return false;
        }

        if (!value) {
            return 'This field is required.';
        }

        const amount = Number(value);
        if (isNaN(amount)) {
            return 'Amount must be a number.';
        }

        let minimumAmount = 1;
        if (this.fullBalance !== undefined && this.fullBalance < 1) {
            minimumAmount = this.fullBalance;
        }
        if (amount < minimumAmount) {
            return `Amount must be at least ${currency(minimumAmount)}.`;
        }

        if (this.fullBalance !== undefined && amount > this.fullBalance) {
            return 'Amount must be less than full balance.';
        }
    };

    isFullBalance = new FieldState(true);
    customAmount = new FieldState('').validators(this.customAmountValidator);

    form = new FormState({
        isFullBalance: this.isFullBalance,
        customAmount: this.customAmount,
    });

    constructor(private props?: RouteComponentProps<RouteParams>,
        private amount?: number
    ) {
        // Initialize with fixed amount
        if (amount) {
            this.fullBalance = amount
            this.isFullBalance.value = true
            return;
        }

        this.form.validate();

        this.isFullBalance.onDidChange(() => {
            this.customAmount.validate();
        });

        const id = props?.match.params.id;
        if (!id) {
            return;
        }

        if (!this.invoiceStore.payment) {
            this.invoiceStore.payment = {
                invoiceNumber: id,
            };
        } else if (this.invoiceStore.payment.invoiceNumber !== id) {
            this.invoiceStore.payment.invoiceNumber = id;
            this.invoiceStore.payment.amount = undefined;
        }

        this.invoiceStore.getInvoice(id).then(
            (invoice) => {
                if (invoice.balance <= 0) {
                    this.router.history.replace(`/invoice/${id}`);
                    return;
                }

                this.fullBalance = invoice.balance;
                this.partialPaymentEnabled = !invoice.partialPaymentDisabled;
            },
            (err) => {
                this.errorMessage = err;
            }
        );
    }

    clearError = () => {
        this.errorMessage = undefined;
    };

    handleSubmit = (e?: React.FormEvent<HTMLFormElement>, isSubcomponent?: boolean) => {
        e && e.preventDefault();

        if (!this.props) {
            // Handle scenario where amount is hardcoded
            return true;
        }

        if (this.form.hasError || !this.fullBalance || !this.invoiceStore.payment) {
            return false;
        }

        this.invoiceStore.payment.amount = this.isFullBalance.$
            ? this.fullBalance
            : Number(this.customAmount.$);

        if (!isSubcomponent) {
            this.props.history.push(`/invoice/${this.props.match.params.id}/payment-info`);
        }

        return true;
    };

    handleFullAmountTrue = () => {
        this.isFullBalance.onChange(true);
        if (this.onStateChanged) {
            this.onStateChanged();
        }
    };

    handleFullAmountFalse = () => {
        this.isFullBalance.onChange(false);
        if (this.onStateChanged) {
            this.onStateChanged();
        }
    };
}

interface RouteParams {
    id: string;
}

interface Props extends RouteComponentProps<RouteParams> {
    isSubcomponent?: boolean;
    data?: PaymentAmountFormState;
}

@observer
export class PaymentAmountContainer extends React.Component<Props> {
    @observable data: PaymentAmountFormState;

    constructor(props: Props) {
        super(props);

        this.data = this.props.data || new PaymentAmountFormState(props);
    }

    render() {
        return <PaymentAmount container={this} />;
    }
}
